/**
 * TypeSpecificEditor Component
 * Renders the appropriate editor based on content type
 * This is a placeholder/router component that will integrate with type-specific editors
 */

import { useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import {
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
} from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FluentCard } from '@/components/layout/page-header'
import { TextElementEditor } from './editors/TextElementEditor'
import { DataPointEditor } from './editors/DataPointEditor'
import { ImageElementEditor } from './editors/ImageElementEditor'
import { TableElementEditor } from './editors/TableElementEditor'
import { EmailSnippetEditor } from './editors/EmailSnippetEditor'
import { SlideElementEditor } from './editors/SlideElementEditor'
import type {
  ContentItemType,
  TextElementContent,
  DataPointContent,
  ImageElementContent,
  TableElementContent,
  EmailSnippetContent,
  SlideElementContent,
} from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface TypeSpecificEditorProps {
  type: ContentItemType
  value: TypeSpecificContent
  onChange: (content: TypeSpecificContent) => void
  disabled?: boolean
  className?: string
}

// Union type for all content types
export type TypeSpecificContent =
  | Partial<TextElementContent>
  | Partial<DataPointContent>
  | Partial<ImageElementContent>
  | Partial<TableElementContent>
  | Partial<EmailSnippetContent>
  | Partial<SlideElementContent>
  | null

// ============================================
// Type Config
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text Element',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email Snippet',
  SlideElement: 'Slide Element',
}

// ============================================
// Text Editor Component
// ============================================

interface TextEditorProps {
  value: Partial<TextElementContent>
  onChange: (content: Partial<TextElementContent>) => void
  disabled?: boolean
}

function TextEditor({ value, onChange, disabled }: TextEditorProps) {
  const [extractedVariables, setExtractedVariables] = useState<string[]>([])

  // Determine if we should use the rich text editor
  const isRichTextMode = value.format === 'RichText' || value.format === 'Html'

  // Handle rich text editor changes
  const handleRichTextChange = useCallback(
    (html: string, plainText: string, variables: string[]) => {
      setExtractedVariables(variables)
      onChange({
        ...value,
        content: html,
        wordCount: plainText.trim().split(/\s+/).filter(Boolean).length,
        characterCount: plainText.length,
      })
    },
    [value, onChange]
  )

  // Handle plain text/markdown changes
  const handlePlainTextChange = useCallback(
    (content: string) => {
      // Extract variables from plain text
      const variableRegex = /\{\{(\w+)\}\}/g
      const vars: string[] = []
      let match: RegExpExecArray | null
      while ((match = variableRegex.exec(content)) !== null) {
        const varName = match[1]
        if (varName && !vars.includes(varName)) {
          vars.push(varName)
        }
      }
      setExtractedVariables(vars)

      onChange({
        ...value,
        content,
        wordCount: content.trim().split(/\s+/).filter(Boolean).length,
        characterCount: content.length,
      })
    },
    [value, onChange]
  )

  return (
    <div className="space-y-4">
      {/* Format Selector */}
      <div className="flex items-center justify-between">
        <Label htmlFor="text-content">Text Content</Label>
        <Select
          value={value.format || 'Plain'}
          onValueChange={(format) =>
            onChange({ ...value, format: format as TextElementContent['format'] })
          }
          disabled={disabled}
        >
          <SelectTrigger className="w-32 h-8 text-xs rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Plain">Plain Text</SelectItem>
            <SelectItem value="RichText">Rich Text</SelectItem>
            <SelectItem value="Markdown">Markdown</SelectItem>
            <SelectItem value="Html">HTML</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rich Text Editor (for RichText and Html formats) */}
      {isRichTextMode ? (
        <TextElementEditor
          value={value.content || ''}
          onChange={handleRichTextChange}
          placeholder="Start typing your content here. Use {{variableName}} for dynamic content..."
          minHeight={250}
          readOnly={disabled}
        />
      ) : (
        /* Plain Text / Markdown Editor */
        <div className="space-y-2">
          <Textarea
            id="text-content"
            placeholder={
              value.format === 'Markdown'
                ? 'Enter your markdown content here. Use {{variableName}} for dynamic content...'
                : 'Enter your text content here. Use {{variableName}} for dynamic content...'
            }
            value={value.content || ''}
            onChange={(e) => handlePlainTextChange(e.target.value)}
            disabled={disabled}
            rows={12}
            className={cn(
              'rounded-xl resize-none text-sm',
              value.format === 'Markdown' && 'font-mono'
            )}
          />
          <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center gap-4">
              <span>{value.characterCount ?? (value.content || '').length} characters</span>
              <span>
                {value.wordCount ?? (value.content || '').split(/\s+/).filter(Boolean).length} words
              </span>
            </div>
            {extractedVariables.length > 0 && (
              <span className="flex items-center gap-1">
                <span className="font-mono text-blue-600 dark:text-blue-400">
                  {extractedVariables.length}
                </span>{' '}
                variable{extractedVariables.length !== 1 ? 's' : ''} found
              </span>
            )}
          </div>
        </div>
      )}

      {/* Language Selector */}
      <div className="space-y-2">
        <Label htmlFor="text-language">Language (optional)</Label>
        <Input
          id="text-language"
          type="text"
          placeholder="e.g., en-US, de-DE"
          value={value.language || ''}
          onChange={(e) => onChange({ ...value, language: e.target.value })}
          disabled={disabled}
          className="rounded-xl max-w-xs"
        />
      </div>

      {/* Help Text for Markdown */}
      {value.format === 'Markdown' && (
        <div className="p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            <strong>Tip:</strong> Use Markdown syntax for formatting:{' '}
            <code className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded text-xs">
              **bold**
            </code>{' '}
            <code className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded text-xs">
              *italic*
            </code>{' '}
            <code className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded text-xs">
              # Heading
            </code>{' '}
            <code className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded text-xs">
              - List item
            </code>
          </p>
        </div>
      )}
    </div>
  )
}

// All content type editors are now imported from ./editors/*

// ============================================
// TypeSpecificEditor Component
// ============================================

export function TypeSpecificEditor({
  type,
  value,
  onChange,
  disabled = false,
  className,
}: TypeSpecificEditorProps) {
  const Icon = TYPE_ICONS[type]
  const color = TYPE_COLORS[type]
  const label = TYPE_LABELS[type]

  const renderEditor = () => {
    switch (type) {
      case 'Text':
        return (
          <TextEditor
            value={(value as Partial<TextElementContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
          />
        )
      case 'DataPoint':
        return (
          <DataPointEditor
            value={(value as Partial<DataPointContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
            className=""
          />
        )
      case 'EmailSnippet':
        return (
          <EmailSnippetEditor
            value={(value as Partial<EmailSnippetContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
          />
        )
      case 'Image':
        return (
          <ImageElementEditor
            value={(value as Partial<ImageElementContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
          />
        )
      case 'Table':
        return (
          <TableElementEditor
            value={(value as Partial<TableElementContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
          />
        )
      case 'SlideElement':
        return (
          <SlideElementEditor
            value={(value as Partial<SlideElementContent>) || {}}
            onChange={(content) => onChange(content)}
            disabled={disabled}
          />
        )
      default:
        return (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            Unknown content type: {type}
          </div>
        )
    }
  }

  return (
    <FluentCard className={className}>
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('p-2 rounded-xl bg-gradient-to-br', color)}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">{label}</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Configure your {label.toLowerCase()} content
          </p>
        </div>
      </div>

      {renderEditor()}
    </FluentCard>
  )
}

export default TypeSpecificEditor
